import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { motion } from 'framer-motion';

const PoojaInformation = () => {
  const deepGolden = "#B8860B";
  const saffronGradient = "linear-gradient(135deg, #FF8C00 0%, #B8860B 100%)";

  const poojaGroups = [
    {
      title: "अष्टद्रव्य संच",
      items: ["शुद्ध केशर अष्टगंध", "अक्षत (धुतलेले तांदूळ)", "पुष्प आणि चंदन"],
      desc: "नित्य देवपूजेसाठी लागणारे हे सर्वात मुख्य द्रव्य आहेत.",
    },
    {
      title: "अभिषेक साहित्य",
      items: ["शुद्ध जल आणि दुध", "सुवर्ण/चांदीचा कलश", "मऊ सुती वस्त्र"],
      desc: "प्रभूंच्या अभिषेकासाठी लागणारी अत्यंत शुद्ध आणि सात्विक साधने.",
    },
    {
      title: "आरती आणि मंगल दीप",
      items: ["पंचारती / एकारती", "शुद्ध गाईचे तूप", "कापूर आणि वात"],
      desc: "भक्तीमय वातावरणासाठी आणि प्रभूंच्या ओवाळणीसाठी लागणारे साहित्य.",
    },
    {
      title: "स्वाध्याय आणि शास्त्र",
      items: ["जिनवाणी (शास्त्र)", "नवकार माळ (१०८ मणी)", "शास्त्र कव्हर आणि स्टँड"],
      desc: "दैनंदिन स्वाध्याय आणि चिंतनासाठी आवश्यक असणारे धार्मिक साहित्य.",
    }
  ];

  // ॲनिमेशन व्हेरिएंट्स
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        
        {/* --- Header Section --- */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Typography variant="overline" sx={{ color: deepGolden, fontWeight: 800, letterSpacing: 2 }}>
              शुद्ध भक्तीची तयारी
            </Typography>
            <Typography variant="h2" sx={{ 
              fontWeight: 800, 
              fontFamily: "'serif'", 
              fontSize: { xs: '1.8rem', md: '2.8rem' }, // हेडिंग साईज कमी केली
              color: '#222', mt: 1
            }}>
              आवश्यक <span style={{ color: '#FF8C00' }}>पूजा साहित्य</span>
            </Typography>
            <Box sx={{ width: '60px', height: '3px', bgcolor: '#FF8C00', mx: 'auto', mt: 2, borderRadius: '2px' }} />
          </motion.div>
        </Box>

        {/* --- Info Cards Grid --- */}
        <Grid 
          container 
          spacing={3} 
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {poojaGroups.map((group, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div variants={cardVariants}>
                <Card 
                  component={motion.div}
                  whileHover={{ 
                    scale: 1.05, 
                    boxShadow: '0 20px 40px rgba(184, 134, 11, 0.15)',
                    borderColor: deepGolden
                  }}
                  sx={{ 
                    height: '100%', 
                    borderRadius: '24px', 
                    boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease-in-out',
                    bgcolor: '#fff'
                  }}
                >
                  {/* Card Top Icon Box with Floating Animation */}
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      background: saffronGradient, 
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                     <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                     >
                        <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 28 }} />
                     </motion.div>
                  </Box>

                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#333', fontSize: '1.25rem', fontFamily: 'serif' }}>
                      {group.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2.5, fontSize: '0.85rem', lineHeight: 1.6 }}>
                      {group.desc}
                    </Typography>
                    
                    <Divider sx={{ mb: 2, opacity: 0.6 }} />

                    {/* Items List */}
                    <List sx={{ mb: 1 }}>
                      {group.items.map((item, i) => (
                        <ListItem key={i} disableGutters sx={{ py: 0.4 }}>
                          <ListItemIcon sx={{ minWidth: '28px' }}>
                            <CheckCircleIcon sx={{ fontSize: '16px', color: deepGolden }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item} 
                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600, color: '#444' }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PoojaInformation;